import { useEffect, useState } from "react"
import type { FC } from "react"
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react"

import Web3Button from "web3/components/Web3Button"
import ErrorBoundary from "common/components/ErrorBoundary"
import type {
  ProposalActionAttemptQuery,
  ProposalActionAttemptQueryVariables,
  Governor,
  Proposal,
} from "query/graphql"
import {
  useProposalSuccededCtaQuery,
  ProposalActionAttemptDocument,
  ProposalActionType,
} from "query/graphql"
import ProposalBlockExplorerURLButton from "proposal/components/ctas/ProposalBlockExplorerURLButton"
import ProposalCancelButton from "proposal/components/ctas/ProposalCancelButton"
import ProposalLearnMoreAboutButton from "proposal/components/ctas/ProposalLearnMoreAboutButton"
import { useWithProposalErrorCatch } from "governance/hooks/useWithProposalErrorCatch"
import { useQueueProposal } from "proposal/hooks/useQueueProposal"
import { useExecuteProposal } from "proposal/hooks/useExecuteProposal"
import { useQuery } from "query/hooks/useQuery"
import { EllipsisVertical } from "ui/components/icons/font-awesome/EllipsisVertical"
import { EXTERNAL_ROUTES, ROUTES } from "common/constants/routes"
import { GrpcErrorType } from "common/helpers/fetcher"
import { useMe } from "user/providers/MeProvider"
import {
  useSafeTransactionV2,
  type FixedSafeMultisigTransactionResponse,
} from "safe/hooks/useSafeTransactionV2"
import ShareToFarcasterButton from "common/components/ShareToFarcasterButton"
import { useOrganization } from "organization/providers/OrganizationProvider"
import { useDescriptionException } from "proposal/hooks/useDescriptionException"
import { FeatureFlag, useGovernanceContext } from "governance/context"
import ShareToLensButton from "common/components/ShareToLensButton"
import { Chain } from "web3/hooks/useMultichainAccount"
import ProposalShareMenuItem from "proposal/components/ctas/ProposalShareMenuItem"

type ProposalSuccededCTAProps = {
  proposalId: string
  slug: string
}
const ProposalSuccededCTA: FC<ProposalSuccededCTAProps> = ({
  proposalId,
  slug,
}) => {
  const { data, isLoading } = useProposalSuccededCtaQuery({
    input: { id: proposalId },
  })

  if (isLoading) return null

  if (!data) return null

  const { proposal } = data
  const hasTimelock = Boolean(proposal.governor?.timelockId)

  return (
    <>
      {hasTimelock ? (
        <QueueButton
          governance={proposal.governor as Governor}
          proposal={proposal as Proposal}
          slug={slug}
        />
      ) : (
        <ExecuteButton
          governance={proposal.governor as Governor}
          proposal={proposal as Proposal}
          slug={slug}
        />
      )}
    </>
  )
}

type QueueButtonProps = {
  proposal: Proposal
  governance: Governor
  slug: string
}
const QueueButton: FC<QueueButtonProps> = ({ proposal, governance, slug }) => {
  const [safeTx, setSafeTx] = useState<
    FixedSafeMultisigTransactionResponse | undefined
  >(undefined)
  const { withErrorCatch } = useWithProposalErrorCatch()

  const { queueProposal } = useQueueProposal({ proposal, governance })

  const me = useMe()

  const { data: proposalActionAttemptData } = useQuery<
    ProposalActionAttemptQuery,
    ProposalActionAttemptQueryVariables
  >({
    query: ProposalActionAttemptDocument,
    variables: {
      input: {
        proposalId: proposal.id,
        type: ProposalActionType.Queue,
      },
    },
    omittedErrors: [GrpcErrorType.NotFound],
  })

  const { getSafeTransaction, pendingTransactions } = useSafeTransactionV2(
    {
      safeId: me?.id as string,
      accountAddress: me?.address,
    },
    { enabled: me?.type === "SAFE" },
  )

  useEffect(() => {
    if (me?.type === "SAFE" && proposalActionAttemptData) {
      const { proposalActionAttempt } = proposalActionAttemptData
      const { txHash } = proposalActionAttempt

      getSafeTransaction(txHash).then((safeTx) => {
        setSafeTx(safeTx)
      })
    }
  }, [getSafeTransaction, me?.type, proposalActionAttemptData])

  const handleQueue = async (): Promise<void> => {
    await withErrorCatch(queueProposal)
  }

  if (proposalActionAttemptData) {
    const { proposalActionAttempt } = proposalActionAttemptData

    const pendingSafeTx = pendingTransactions.find((pendingTx) => {
      if (pendingTx.safeTxHash === safeTx?.safeTxHash) {
        return pendingTx
      }
    })

    if (Boolean(pendingSafeTx)) {
      return null
    }

    return (
      <Stack
        align="center"
        color="gray.600"
        direction="row"
        justify={{ base: "center", lg: "right" }}
        spacing={4}
        width="100%"
      >
        <Stack
          align="center"
          direction="row"
          spacing={3}
          width={{ base: "100%", sm: "auto" }}
        >
          <Spinner />
          <Text textStyle="body.bold.lg">
            {me?.type === "SAFE" ? "Waiting for signers" : "Queueing proposal"}
          </Text>
        </Stack>

        <OptionsList
          displayLearnAboutQueuing
          governance={governance}
          proposal={proposal}
          proposalActionAttempt={proposalActionAttempt}
          slug={slug}
        />
      </Stack>
    )
  }

  return (
    <ErrorBoundary section="ProposalQueueButton">
      <Stack
        align="center"
        color="gray.600"
        direction="row"
        justify={{ base: "center", lg: "right" }}
        spacing={2}
        width="100%"
      >
        <Web3Button
          px={10}
          requiredChainId={governance.chainId}
          variant="primary"
          walletType={Chain.EVM}
          width={{ base: "100%", sm: "auto" }}
          onValid={handleQueue}
        >
          Queue
        </Web3Button>
        <OptionsList
          displayLearnAboutQueuing
          governance={governance}
          proposal={proposal}
          slug={slug}
        />
      </Stack>
    </ErrorBoundary>
  )
}

type ExecuteButtonProps = {
  proposal: Proposal
  governance: Governor
  slug: string
}
const ExecuteButton: FC<ExecuteButtonProps> = ({
  proposal,
  governance,
  slug,
}) => {
  const { withErrorCatch } = useWithProposalErrorCatch()

  const { executeProposal } = useExecuteProposal({ proposal, governance })

  const handleExecute = async (): Promise<void> => {
    await withErrorCatch(executeProposal)
  }

  const { data: proposalActionAttemptData } = useQuery<
    ProposalActionAttemptQuery,
    ProposalActionAttemptQueryVariables
  >({
    query: ProposalActionAttemptDocument,
    variables: {
      input: {
        proposalId: proposal.id,
        type: ProposalActionType.Execute,
      },
    },
    omittedErrors: [GrpcErrorType.NotFound],
  })

  const { data: proposalActionAttemptCancelData } = useQuery<
    ProposalActionAttemptQuery,
    ProposalActionAttemptQueryVariables
  >({
    query: ProposalActionAttemptDocument,
    variables: {
      input: {
        proposalId: proposal.id,
        type: ProposalActionType.Cancel,
      },
    },
    omittedErrors: [GrpcErrorType.NotFound],
  })

  if (proposalActionAttemptData || proposalActionAttemptCancelData) {
    const proposalActionAttempt = proposalActionAttemptData
      ? proposalActionAttemptData.proposalActionAttempt
      : proposalActionAttemptCancelData?.proposalActionAttempt

    return (
      <Stack
        align="center"
        color="gray.600"
        direction="row"
        justify={{ base: "center", lg: "right" }}
        spacing={4}
        width="100%"
      >
        <Stack
          align="center"
          direction="row"
          spacing={3}
          width={{ base: "100%", sm: "auto" }}
        >
          <Spinner />
          {proposalActionAttemptData ? (
            <Text textStyle="body.bold.lg">Executing Proposal</Text>
          ) : null}
          {proposalActionAttemptCancelData ? (
            <Text textStyle="body.bold.lg">Canceling Proposal</Text>
          ) : null}
        </Stack>

        <OptionsList
          displayLearnAboutExecute
          governance={governance}
          proposal={proposal}
          proposalActionAttempt={proposalActionAttempt}
          slug={slug}
        />
      </Stack>
    )
  }

  return (
    <ErrorBoundary section="ProposalExecuteButton">
      <Stack
        align="center"
        color="gray.600"
        direction="row"
        justify={{ base: "center", lg: "right" }}
        spacing={2}
        width="100%"
      >
        <Web3Button
          px={10}
          requiredChainId={governance.chainId}
          variant="primary"
          walletType={Chain.EVM}
          width={{ base: "100%", sm: "auto" }}
          onValid={handleExecute}
        >
          Execute
        </Web3Button>
        <OptionsList
          displayLearnAboutExecute
          governance={governance}
          proposal={proposal}
          slug={slug}
        />
      </Stack>
    </ErrorBoundary>
  )
}

type OptionsListProps = {
  proposal: Proposal
  governance: Governor
  proposalActionAttempt?: ProposalActionAttemptQuery["proposalActionAttempt"]
  displayLearnAboutQueuing?: boolean
  displayLearnAboutExecute?: boolean
  slug: string
}
const OptionsList: FC<OptionsListProps> = ({
  governance,
  proposal,
  proposalActionAttempt,
  displayLearnAboutQueuing = false,
  displayLearnAboutExecute = false,
  slug,
}) => {
  const { organization } = useOrganization()
  const { title } = useDescriptionException({
    governanceId: governance.id,
    proposal,
  })

  const { isFeatureFlagOn } = useGovernanceContext()
  const hideCancelProposal = isFeatureFlagOn(FeatureFlag.HideCancelProposal)

  return (
    <Flex>
      <Menu>
        <MenuButton
          as={Button}
          bgColor="white"
          borderColor="gray.300"
          borderWidth="1px"
          px={3}
        >
          <Icon as={EllipsisVertical} fill="gray.600" />
        </MenuButton>
        <MenuList>
          {organization && proposal?.onchainId ? (
            <ProposalShareMenuItem
              url={ROUTES.governance.proposal.id(
                slug,
                proposal.onchainId,
                organization.governorIds.length && proposal.governor
                  ? proposal.governor.id
                  : "",
              )}
            />
          ) : null}

          {organization && title ? (
            <>
              <MenuItem>
                <ShareToFarcasterButton
                  size="sm"
                  title={`Tally | ${organization.name} | ${title}`}
                  url={ROUTES.governance.proposal.id(
                    organization.slug,
                    proposal.id,
                    organization.governorIds.length && proposal.governor
                      ? proposal.governor.id
                      : "",
                  )}
                  variant="tertiary"
                />
              </MenuItem>
              <MenuItem>
                <ShareToLensButton
                  size="sm"
                  title={`Tally | ${organization.name} | ${title}`}
                  url={ROUTES.governance.proposal.id(
                    organization.slug,
                    proposal.id,
                    organization.governorIds.length && proposal.governor
                      ? proposal.governor.id
                      : "",
                  )}
                  variant="tertiary"
                />
              </MenuItem>
            </>
          ) : null}

          {proposalActionAttempt?.txHash && proposalActionAttempt?.chainId ? (
            <MenuItem>
              <ProposalBlockExplorerURLButton
                chainId={proposalActionAttempt.chainId}
                txHash={proposalActionAttempt.txHash}
              />
            </MenuItem>
          ) : null}
          {!hideCancelProposal ? (
            <MenuItem>
              <ProposalCancelButton
                governance={governance}
                proposal={proposal}
              />
            </MenuItem>
          ) : null}
          {displayLearnAboutQueuing ? (
            <MenuItem>
              <ProposalLearnMoreAboutButton
                label={`Learn more about "Queuing"`}
                url={EXTERNAL_ROUTES.tally.docs.queuingProposals()}
              />
            </MenuItem>
          ) : null}
          {displayLearnAboutExecute ? (
            <MenuItem>
              <ProposalLearnMoreAboutButton
                label={`Learn more about "Execute"`}
                url={EXTERNAL_ROUTES.tally.docs.executingProposals()}
              />
            </MenuItem>
          ) : null}
        </MenuList>
      </Menu>
    </Flex>
  )
}

export default ProposalSuccededCTA
